import { Col, Container, Row } from "reactstrap";
import s from "../organizationSetup/styles.scss";
import WizardBreadcrumb from "../common/WizardBreadcrumb";
import React, { useState, useEffect, useCallback } from "react";
import { Wizard } from "react-use-wizard";
import WizardStepper from "../components/WizardStepper";
import StepTransition from "../components/StepTransition";
import { WizardStep } from "../common/types";
import withStyles from "isomorphic-style-loader/withStyles";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneNumberMethodSelection, { phoneNumberMethodSelectionSchema } from "./phoneNumberMethodSelection";
import AreaCodeInput, { areaCodeInputSchema } from "./areaCodeInput";
import PortNumberCheck, { portNumberCheckSchema } from "./portNumberCheck";
import ConfirmationStep from "./confirmationStep";
import { FormProvider, useForm, Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { serviceCaller, ServiceCallerProps } from "../../../../composition";
import { connect } from "react-redux";
import LoadingSpinner from "../../../../../toolkit/loadingSpinner";
import SnackbarNotification from "../../../../../toolkit/snackbarNotification";
import { useMmsSetupData } from "./hooks/useMmsSetupData";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { clearMmsSenderAreaCode, cancelMmsOnboarding, resumeMmsOnboarding } from "../../../../../utils/mmsOnboardingApi";

const allWizardSteps: WizardStep[] = [
  {
    label: "Phone Number Method",
    icon: <PhoneIcon fontSize="small" />,
    Component: PhoneNumberMethodSelection,
    schema: phoneNumberMethodSelectionSchema,
  },
  {
    label: "Configure Number",
    icon: <PhoneIcon fontSize="small" />,
    Component: AreaCodeInput,
    schema: areaCodeInputSchema,
  },
  {
    label: "Configure Number",
    icon: <PhoneIcon fontSize="small" />,
    Component: PortNumberCheck,
    schema: portNumberCheckSchema,
  },
];

const mmsSetupWizardSchema = yup.object().shape(
  allWizardSteps.reduce((acc, step) => ({
    ...acc,
    ...step.schema.fields
  }), {})
);

interface MmsSetupWizardProps extends ServiceCallerProps {
  embedded?: boolean;
  onExit?: () => void;
  charityId?: string;
}

interface MmsWizardFormValues {
  phoneNumberMethod: string;
  areaCode: string;
  phoneNumber: string;
  useSharedTcr: boolean;
  sharedTcrEntryId: string;
}

const MmsSetupWizard: React.FC<MmsSetupWizardProps> = ({
  embedded = true,
  onExit,
  charityId,
  config,
  serviceAccessToken,
}) => {
  const { snackbarProps, showSnackbar } = useSnackbar();
  const [isEditMode, setIsEditMode] = useState(false);

  const wizardSteps: WizardStep[] = [
    {
      label: "Phone Number Method",
      icon: <PhoneIcon fontSize="small" />,
      Component: PhoneNumberMethodSelection,
      schema: phoneNumberMethodSelectionSchema,
    },
    {
      label: "Configure Number",
      icon: <PhoneIcon fontSize="small" />,
      Component: AreaCodeInput,
      schema: areaCodeInputSchema,
    },
  ];

  const methods = useForm<MmsWizardFormValues>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: yupResolver(mmsSetupWizardSchema) as unknown as Resolver<MmsWizardFormValues>,
    defaultValues: {
      phoneNumberMethod: "",
      areaCode: "",
      phoneNumber: "",
      useSharedTcr: false,
      sharedTcrEntryId: "",
    },
  });

  const { loading, mmsSetupData, error, refresh } = useMmsSetupData({
    charityId,
  });

  const [resumeAfterSave, setResumeAfterSave] = useState(false);

  const handleConfigurationSaved = useCallback(async () => {
    if (resumeAfterSave && charityId && config?.adminServiceUrl && serviceAccessToken) {
      await resumeMmsOnboarding(config.adminServiceUrl, charityId, serviceAccessToken);
      setResumeAfterSave(false);
      await refresh();
      setIsEditMode(false);
      showSnackbar("Area code updated. MMS setup resumed.", "success");
      return;
    }
    await refresh();
    setIsEditMode(false);
    showSnackbar("MMS configuration saved successfully.", "success");
  }, [resumeAfterSave, charityId, config, serviceAccessToken, refresh, showSnackbar]);

  useEffect(() => {
    if (mmsSetupData && mmsSetupData.mmsSenderAreaCode) {
      methods.reset({
        phoneNumberMethod: methods.getValues("phoneNumberMethod") || "",
        areaCode: mmsSetupData.mmsSenderAreaCode,
        phoneNumber: mmsSetupData.mmsSenderAreaCode,
        useSharedTcr: mmsSetupData.useSharedTcr || false,
        sharedTcrEntryId: mmsSetupData.sharedTcr?.entryId || "",
      });
    }
  }, [mmsSetupData, methods]);

  const phoneNumberMethod = methods.watch("phoneNumberMethod");

  const hasStatusView =
    mmsSetupData?.status === "pending" ||
    mmsSetupData?.status === "completed" ||
    !!mmsSetupData?.error;
  const showStandaloneBreadcrumb =
    !embedded && (loading || (hasStatusView && !isEditMode));

  const renderMainContent = () => {
    if (error) {
      return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "300px" }}
        >
          <div className="text-center">
            <h5 className="text-danger">Unable to load MMS setup</h5>
            <p className="text-muted">{error}</p>
          </div>
        </div>
      );
    }

    const hasStatus = mmsSetupData?.status === 'pending' || mmsSetupData?.status === 'completed';
    const hasError = !!mmsSetupData?.error;
    if ((hasStatus || hasError) && !isEditMode) {
      return (
        <div className={embedded ? undefined : s.wizardStepPanelStandalone}>
          <ConfirmationStep
          mmsSetupData={mmsSetupData}
          charityId={charityId}
          config={config}
          serviceAccessToken={serviceAccessToken}
          onEdit={() => setIsEditMode(true)}
          onChangeAreaCode={() => {
            methods.setValue("phoneNumberMethod", "newNumber");
            methods.setValue("areaCode", "");
            setResumeAfterSave(true);
            setIsEditMode(true);
          }}
          onExit={onExit}
          onRefreshSetupData={refresh}
          showSnackbar={showSnackbar}
          onResume={async () => {
            if (!charityId || !config?.adminServiceUrl || !serviceAccessToken) {
              throw new Error("Missing configuration. Please try again.");
            }
            await resumeMmsOnboarding(
              config.adminServiceUrl,
              charityId,
              serviceAccessToken
            );
          }}
          onCancel={async () => {
            if (!charityId || !config?.adminServiceUrl || !serviceAccessToken) {
              throw new Error("Missing configuration. Please try again.");
            }
            await cancelMmsOnboarding(
              config.adminServiceUrl,
              charityId,
              serviceAccessToken
            );
            if (config.adminServiceUrl && serviceAccessToken) {
              await clearMmsSenderAreaCode(
                config.adminServiceUrl,
                charityId,
                serviceAccessToken
              );
            }
            methods.reset({
              phoneNumberMethod: "",
              areaCode: "",
              phoneNumber: "",
              useSharedTcr: false,
              sharedTcrEntryId: "",
            });
            setIsEditMode(true);
          }}
        />
        </div>
      );
    }

    return (
      <FormProvider {...methods}>
        <form noValidate className={s.fadeIn}>
          <Wizard
            startIndex={0}
            header={
              <WizardStepper
                steps={wizardSteps}
                onBack={onExit}
                showBack={!embedded}
              />
            }
            wrapper={
              <StepTransition
                contentClassName={embedded ? undefined : s.wizardStepPanel}
              />
            }
          >
            <PhoneNumberMethodSelection />

            {phoneNumberMethod === "portNumber" ? (
              <PortNumberCheck
                charityId={charityId}
                config={config}
                serviceAccessToken={serviceAccessToken}
                onConfigurationSaved={handleConfigurationSaved}
                showSnackbar={showSnackbar}
              />
            ) : (
              <AreaCodeInput
                charityId={charityId}
                config={config}
                serviceAccessToken={serviceAccessToken}
                onConfigurationSaved={handleConfigurationSaved}
                showSnackbar={showSnackbar}
              />
            )}
          </Wizard>
        </form>
      </FormProvider>
    );
  };

  return (
    <>
      <Container fluid className={embedded ? "vh-100" : s.fullScreenContainer}>
        <Row className="h-100">
          <Col md="12" className={s.wizardContainer}>
            {showStandaloneBreadcrumb && (
              <WizardBreadcrumb onBack={onExit} showBreadcrumb />
            )}
            {loading ? (
              <div className="mt-5">
                <LoadingSpinner
                  title="Loading MMS Setup"
                  message="Please wait while we load your data and initialize the wizard."
                  className={s.fadeIn}
                />
              </div>
            ) : (
              <div className={embedded ? undefined : s.wizardShell}>
                {renderMainContent()}
              </div>
            )}
          </Col>
        </Row>
      </Container>

      <SnackbarNotification
        {...snackbarProps}
        position={{ vertical: "top", horizontal: "center" }}
      />
    </>
  );
};

const mapStateToProps = (state: any) => ({
  charityId: state.user.currentCharity?.id,
});

export default connect(mapStateToProps)(
  serviceCaller(withStyles(s)(MmsSetupWizard))
);
